import { InputDate, InputDigits, InputFile } from '@hub/shared'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Invoice {
  emissionDate: string
  serialNumber: string
  file: any
}

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  onChange?: (value: any) => any
  onValidate?: (value: boolean) => any
}

export default ({ onChange, onValidate }: Props) => {
  const [t] = useTranslation('uploadInvoice')

  const [state, setState] = useState<Invoice>({ emissionDate: '', serialNumber: '', file: null })
  const [errors, setErrors] = useState<Invoice>({ emissionDate: '', serialNumber: '', file: '' })

  const today = new Date().toISOString().split('T')[0]

  useEffect(() => {
    if (onChange && typeof onChange === 'function') {
      onChange(state)
    }
  }, [state, onChange])

  useEffect(() => {
    if (onValidate && typeof onValidate === 'function') {
      onValidate(
        !Object.keys(errors)?.some((field) => (errors as any)[field]) && Object.keys(state)?.every((field) => (state as any)[field])
      )
    }
  }, [state, errors, onValidate])

  const handleChange = useCallback(
    (field: string, value: any) => {
      if (field === 'file' && (value.length === 0 || value[0]?.type !== 'application/pdf')) {
        setErrors({ ...errors, [field]: t('invalidField') })
      } else if (['emissionDate', 'serialNumber'].includes(field) && !value) {
        setErrors({ ...errors, [field]: t('requiredField') })
      } else if (field === 'emissionDate' && value?.length === 10 && new Date(value) > new Date(today)) {
        setErrors({ ...errors, [field]: t('biggerThanToday') })
      } else {
        setErrors({ ...errors, [field]: '' })
      }

      setState({ ...state, [field]: value })
    },
    [state, errors, today, t]
  )

  return (
    <section className="upload-invoice">
      <h5>{t('title')}</h5>
      <form>
        <InputDate
          className="mb-3"
          name="input-invoice-emission-date"
          id="input-invoice-emission-date"
          label={t('inputDate.label')}
          placeholder={t('inputDate.placeholder')}
          externalError={errors.emissionDate}
          value={state.emissionDate}
          onChange={(value) => handleChange('emissionDate', value)}
          type="date"
          maxDate={today}
          requiredOnLoad
          required
        />
        <InputDigits
          className="mb-4"
          name="input-invoice-serial-number"
          id="input-invoice-serial-number"
          label={t('inputDigits.label')}
          placeholder={t('inputDigits.placeholder')}
          externalError={errors.serialNumber}
          value={state.serialNumber}
          onChange={(value) => handleChange('serialNumber', value)}
          maxLength={10}
          required
          requiredOnLoad
        />
        <InputFile
          id="input-invoice-file"
          name="input-invoice-file"
          onChange={(value) => handleChange('file', value)}
          placeholder={t('inputFile')}
          textColor="var(--primary_color)"
          backgroundColor="transparent"
          borderColor="var(--primary_color)"
          externalError={errors.file}
          buttonInverted
          buttonStyle={{ width: '100%', textAlign: 'center' }}
          accept=".pdf"
          type="file"
          errorSpan={!!errors.file}
        />
      </form>
    </section>
  )
}
